<template>
  <div>
    <div class="pb-300">
      <h3 class="fs-400">
        {{ $t('sfCustomObjectProductMappings.title') }}
      </h3>
    </div>
    <div class="pb-300">
      <div class="header-text">
        {{ $t('sfCustomObjectProductMappings.description') }}
      </div>
    </div>

    <!-- カスタムオブジェクト選択 -->
    <div
      v-loading="loadingObjects"
      class="pb-300"
    >
      <div v-if="customObjects.length > 0">
        <BSelect
          v-model="selectedCustomObject"
          class="select-box"
          :items="customObjectsForSelect"
          item-text="label"
          item-value="value"
          :placeholder="$t('sfCustomObjectProductMappings.selectCustomObjectPlaceholder')"
          filterable
          @change="onCustomObjectChange"
        />
      </div>
      <div
        v-else-if="!loadingObjects"
        class="no-data-message"
      >
        {{ $t('sfCustomObjectProductMappings.noCustomObjects') }}
      </div>
    </div>

    <!-- 取引責任者関連項目選択 -->
    <div
      v-if="selectedCustomObject"
      class="pb-300"
    >
      <div class="pb-100">
        <span class="label-text">{{ $t('sfCustomObjectProductMappings.selectContactRelation') }}</span>
      </div>
      <BSelect
        v-model="selectedContactRelationField"
        class="select-box"
        :items="contactRelationFieldsForSelect"
        item-text="label"
        item-value="value"
        :placeholder="$t('sfCustomObjectProductMappings.selectContactRelationPlaceholder')"
        filterable
      />
      <div class="pt-300">
        <div class="header-text">
          {{ $t('sfCustomObjectProductMappings.contactDescription') }}
        </div>
      </div>
    </div>

    <!-- マッピングテーブル -->
    <div
      v-if="selectedCustomObject && mappings"
      v-loading="loading"
      class="pb-300"
    >
      <div class="pb-200">
        <h4>{{ $t('sfCustomObjectProductMappings.mappingSettings') }}</h4>
      </div>

      <!-- ヘッダー -->
      <BLayout
        class="pb-200 header"
        justify-center
        align-center
      >
        <BListItem class="radio-priority-area">
          <div class="header-text-center">
            <span>{{ $t('sfCustomObjectProductMappings.bcPriority') }}</span>
          </div>
        </BListItem>
        <BListItem>
          <div class="header-text-left">
            <span>{{ $t('sfCustomObjectProductMappings.salesforceField') }}</span>
          </div>
        </BListItem>
        <div class="icon" />
        <BListItem>
          <div class="header-text-left">
            <span>{{ $t('sfCustomObjectProductMappings.balesField') }}</span>
          </div>
        </BListItem>
        <BListItem class="check-area">
          <div class="header-text-center">
            <span>{{ $t('sfCustomObjectProductMappings.sfImport') }}</span>
          </div>
        </BListItem>
        <BListItem class="check-area">
          <div class="header-text-center">
            <span>{{ $t('sfCustomObjectProductMappings.bcImport') }}</span>
          </div>
        </BListItem>
      </BLayout>

      <!-- マッピング行 -->
      <BLayout
        v-for="(value, bcKey) in mappings"
        :key="bcKey"
        class="pb-200"
        justify-center
        align-center
      >
        <!-- BC優先（固定） -->
        <BListItem class="radio-priority-area">
          <div
            :class="`column-content non-border radio-box radio-box-${value['sf_column'] ? 'active' : 'non-active'} ${!value['sf_column'] ? 'disabled' : ''}`"
          >
            <BIcon
              class="radio"
              size="b-icon-small"
            >
              done
            </BIcon>
          </div>
        </BListItem>

        <!-- Salesforce項目選択 -->
        <BListItem>
          <BSelect
            v-model="value['sf_column']"
            filterable
            clearable
            class="select-column"
            :items="objectFieldsForSelect"
            item-text="label"
            item-value="value"
            fit
            :placeholder="$t('general.select.please')"
            @change="onMappingChange"
          />
        </BListItem>

        <div class="icon">
          <BIcon
            class="icon-sync"
            size="b-icon-medium"
          >
            swap_horiz
          </BIcon>
        </div>

        <!-- BALES CLOUD項目 -->
        <BListItem>
          <div class="column-content non-border bc-label">
            <span>{{ value['label'] || bcKey }}</span>
          </div>
        </BListItem>

        <!-- SF→BC チェックボックス（固定） -->
        <BListItem class="check-area">
          <BCheckbox
            v-model="value['sf_import']"
            class="column-content non-border check-box"
            :disabled="true"
          />
        </BListItem>

        <!-- SF←BC チェックボックス（固定） -->
        <BListItem class="check-area">
          <BCheckbox
            v-model="value['bc_import']"
            class="column-content non-border check-box"
            :disabled="true"
          />
        </BListItem>
      </BLayout>
    </div>

    <!-- 固定フッター -->
    <div
      v-if="selectedCustomObject"
      class="footer"
    >
      <div
        class="btn-back"
        @click="backAction"
      >
        {{ $t("sfObjectMappings.back") }}
      </div>
      <div
        class="btn-save"
        @click="saveMapping"
      >
        {{ $t("sfObjectMappings.save") }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { SfCustomObjectProductMappingApiService } from '@/api/user/resources/sf_custom_object_product_mapping';
import { useToast } from '@/composable/toast';

const emit = defineEmits(['back']);

const i18n = useI18n();
const { successToast, errorToast } = useToast();

const api = new SfCustomObjectProductMappingApiService();

const customObjects = ref<string[]>([]);
const selectedCustomObject = ref<string>('');
const existingCustomObjectName = ref<string | null>(null);
interface ContactRelationField {
  name: string;
  label: string;
}

interface MappingValue {
  label?: string;
  sf_column: string;
  sf_import: boolean;
  bc_import: boolean;
  bc_priority: boolean;
}

interface Mappings {
  [key: string]: MappingValue;
}

interface ObjectFields {
  [key: string]: {
    label: string;
    type: string;
  };
}

const contactRelationFields = ref<ContactRelationField[]>([]);
const selectedContactRelationField = ref<string>('');
const mappings = ref<Mappings | null>(null);
const objectFields = ref<ObjectFields>({});
const loadingObjects = ref(false);
const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isNewMapping = ref(true);

const customObjectsForSelect = computed(() => {
  return customObjects.value.map((obj) => ({
    label: obj,
    value: obj,
  }));
});

const contactRelationFieldsForSelect = computed(() => {
  return contactRelationFields.value.map((field) => ({
    label: `${field.label} (${field.name})`,
    value: field.name,
  }));
});

const objectFieldsForSelect = computed(() => {
  if (!objectFields.value) return [];
  return Object.keys(objectFields.value).map((fieldName) => ({
    label: `${objectFields.value[fieldName].label} (${fieldName})`,
    value: fieldName,
  }));
});

const backAction = () => {
  emit('back');
};

const onMappingChange = () => {
  // マッピング変更時の処理（必要に応じて追加）
};

const onCustomObjectChange = async (newValue: string) => {
  if (!newValue) return;
  selectedContactRelationField.value = '';
  mappings.value = null;
  await Promise.all([loadContactRelationFields(), loadMapping()]);
};

const loadCustomObjects = async () => {
  loadingObjects.value = true;
  try {
    const response = await api.getSfCustomObjects();
    customObjects.value = response.data.custom_objects || [];
    const existing = response.data.existing_custom_object_name ?? null;
    existingCustomObjectName.value = existing;
    if (existing) {
      selectedCustomObject.value = existing;
      await Promise.all([loadContactRelationFields(), loadMapping()]);
    }
  } catch (_error) {
    console.error('Failed to load custom objects:', _error);
    errorToast(i18n.t('general.fail.to', { action: i18n.t('general.read.text') }));
  } finally {
    loadingObjects.value = false;
  }
};

const loadContactRelationFields = async () => {
  if (!selectedCustomObject.value) return;
  try {
    const response = await api.getSfContactRelationFields({
      request: { customObjectName: selectedCustomObject.value },
    });
    const fields = response.data.contact_relation_fields || [];
    contactRelationFields.value = fields.filter((f): f is ContactRelationField => !!f.name && !!f.label);
  } catch (_error) {
    console.error('Failed to load contact relation fields:', _error);
    errorToast(i18n.t('general.fail.to', { action: i18n.t('general.read.text') }));
  }
};

const loadMapping = async () => {
  if (!selectedCustomObject.value) return;
  loading.value = true;
  try {
    const response = await api.getSfCustomObjectProductMapping({
      request: { customObjectName: selectedCustomObject.value },
    });
    const rawMappings = (response.data.mappings || null) as Mappings | null;
    if (rawMappings) {
      Object.keys(rawMappings).forEach((key) => {
        rawMappings[key].bc_priority = true;
        rawMappings[key].sf_import = true;
        rawMappings[key].bc_import = true;
      });
    }
    mappings.value = rawMappings;
    objectFields.value = (response.data.object_fields || {}) as ObjectFields;
    selectedContactRelationField.value = response.data.contact_relation_field || '';
    isNewMapping.value = !response.data.contact_relation_field;
  } catch (_error) {
    errorToast(i18n.t('general.fail.to', { action: i18n.t('general.read.text') }));
  } finally {
    loading.value = false;
  }
};


const saveMapping = async () => {
  if (!selectedCustomObject.value) return;
  saving.value = true;
  try {
    const requestData = {
      custom_object_name: selectedCustomObject.value,
      mapping: JSON.stringify(mappings.value),
      contact_relation_field: selectedContactRelationField.value,
    };
    await api.upsertSfCustomObjectProductMapping({
      request: { upsertSfCustomObjectProductMappingRequest: requestData },
    });
    successToast(i18n.t('general.success.to', { action: i18n.t('general.save.text') }));
    isNewMapping.value = false;
  } catch (_error) {
    errorToast(i18n.t('general.fail.to', { action: i18n.t('general.save.text') }));
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  await loadCustomObjects();
});
</script>

<style lang="scss" scoped>
.header-text {
  color: $textcolor-light;
  white-space: pre-wrap;
}

.label-text {
  color: $textcolor-light;
}

.select-box {
  width: 100%;
}

.no-data-message {
  color: $textcolor-light;
  padding: 12px;
  border-radius: 4px;
}

.header {
  color: $concrete-dark;
  font-size: 14px;
  font-weight: 400;
}

.header-text-center {
  width: 100%;
  text-align: center;
}

.header-text-left {
  width: 100%;
  text-align: left;
  padding-left: 12px;
}

.column-content {
  width: 200px;
  text-align: center;
}

.non-border {
  border-width: 0;
}

.radio-priority-area {
  width: 60px !important;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  text-align: center;
}

.check-area {
  width: 80px !important;
  margin: 0 20px;
  flex-shrink: 0;
  text-align: center;
}

.select-column {
  width: 100%;
  min-width: 250px;
}

.icon {
  display: flex;
  width: 20%;
  align-items: center;
  justify-content: center;
  font-size: 32px !important;
  color: $concrete-dark !important;
  padding: 0 12px;

  .icon-sync {
    color: $concrete-dark !important;
  }
}

.radio-box {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  cursor: pointer;
  border-radius: 50%;

  > .radio {
    color: $textcolor-white;
  }

  &.radio-box-active {
    background-color: $basecolor-success;
  }

  &.radio-box-non-active {
    background-color: $concrete-dark;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.bc-label {
  font-weight: normal;
  text-align: left;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  width: calc(100% - 310px);
  height: 63px;
  background-color: #F6F6F6;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.btn-back {
  color: #231815;
  font-size: 18px;
  margin: 0 12px;
  padding: 4px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.btn-save {
  width: 101px;
  height: 37px;
  border-radius: 4px;
  background: #018485;
  margin: 0 12px;
  color: #FFF;
  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}

.check-box {
  --el-checkbox-input-height: 20px;
  --el-checkbox-input-width: 20px;
  --el-checkbox-border-radius: 4px;
  --el-index-normal: 0;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  cursor: pointer;

  :deep(.el-checkbox__input) {
    .el-checkbox__inner::after{
      width: 9px;
      height: 10px;
      border: 2px solid transparent;
      border-left: 0;
      border-top: 0;
      border-color: var(--el-checkbox-checked-icon-color);
      transform: rotate(45deg) scaleY(1);
      top: 0;
      left: 22%;
    }
  }
}
</style>
